import moment from "moment";
import isEqual from "lodash/isEqual";
import snakeCase from "lodash/snakeCase";

import displayFields from "../constants/displayFields";
import { serverFormatWithTimezone } from "./dates";

export const formatObject = (values) => Object.keys(values).filter((key) => values[key]);

const SKIP_FIELDS = ["descriptive_info", "origin_info", "destination_info"];
const DATE_FORMAT = "MM/DD/YYYY";
const DATE_INPUT_FORMATS = [
  DATE_FORMAT,
  "YYYY-MM-DD",
  serverFormatWithTimezone,
  "YYYY-MM-DDTHH:mm:ss Z",
  "YYYY-MM-DD HH:mm:ss UTC",
];

const formatMonthDayYear = (value) => {
  if (!value || value === "N/A") return value || "";

  const strictDate = moment(value, DATE_INPUT_FORMATS, true);
  if (strictDate.isValid()) return strictDate.format(DATE_FORMAT);

  const fallbackDate = moment(value);
  return fallbackDate.isValid() ? fallbackDate.format(DATE_FORMAT) : value;
};

const formatDateForServer = (value) => {
  if (!value || value === "N/A") return value || "";

  const strictDate = moment(value, DATE_INPUT_FORMATS, true);
  if (strictDate.isValid()) return strictDate.format("YYYY-MM-DD");

  const fallbackDate = moment(value);
  return fallbackDate.isValid() ? fallbackDate.format("YYYY-MM-DD") : value;
};

export const getInitImage = (asset) =>
  asset.images && asset.images.length > 0 ? asset.images[0].id : "";

export const initialDescription = {
  description: "",
  origin_qty: "",
  dest_qty: "",
  dest_room: "",
};

export const initialPhysicalDimensions = {
  depth: "",
  width: "",
  height: "",
};

export const formatAssetInput = (assetInput) => {
  const { attachments, ...input } = assetInput;

  if (input.quantity !== null && input.quantity !== undefined && input.quantity !== "") {
    input.quantity = parseInt(input.quantity);
  }

  return input;
};

const CREATE_ASSET_EXCLUDED_KEYS = [
  "copyLocation",
  "skipNew",
  "copyAll",
  "attachments",
  "__typename",
  "id",
  "availableQuantity",
  "barcodes",
  "fileUrl",
  "images",
  "nextAssetId",
  "offline",
  "photo",
  "prevAssetId",
  "reservedQuantity",
  "allocatedQuantity",
  "inCartQuantity",
  "warehouseQuantity",
  "assetId",
  "project",
  "client",
  "updated",
  "updatedAt",
  "createdAt",
  "lockVersion",
  "changedFields",
  "itemId",
  "type",
];

const CLIENT_ONLY_KEYS = CREATE_ASSET_EXCLUDED_KEYS;

/**
 * Compares current form values against the initial values snapshot and returns
 * only the fields whose values actually changed.  This keeps mutation payloads
 * sparse so concurrent editors don't overwrite each other's work.
 *
 * - Uses lodash isEqual for deep comparison (handles notes, rackLocations, etc.)
 * - Strips client-only keys that should never be sent to the server
 * - Returns an empty object when nothing changed (callers should skip the mutation)
 */
export const buildAssetPatch = (currentValues, initialValues) => {
  const patch = {};
  for (const key of Object.keys(currentValues)) {
    if (CLIENT_ONLY_KEYS.includes(key)) continue;
    if (!isEqual(currentValues[key], initialValues[key])) {
      patch[key] = currentValues[key];
    }
  }
  return patch;
};

export const buildAssetBaseValues = (patch, initialValues) => {
  const baseValues = {};
  for (const key of Object.keys(patch)) {
    baseValues[key] = initialValues[key];
  }
  return baseValues;
};

/**
 * Merges a subscription payload into an existing asset, respecting the
 * changedFields list so that only the fields the server actually modified
 * are applied.  When changedFields is absent (old server, or asset creation),
 * falls back to full-object replacement for backward compatibility.
 *
 * @param {Object} existing     - The current local asset object
 * @param {Object} incoming     - The subscription payload
 * @param {string[]|null} changedFields - Which fields were modified, or null for full replace
 * @param {string[]} [dirtyFields] - Field names the local user is currently editing
 * @returns {{ merged: Object, conflicts: string[] }}
 */
export const mergeAssetPatch = (existing, incoming, changedFields, dirtyFields = []) => {
  if (!changedFields || changedFields.length === 0) {
    return { merged: { ...existing, ...incoming }, conflicts: [] };
  }

  const merged = { ...existing };
  const conflicts = [];

  for (const field of changedFields) {
    if (dirtyFields.includes(field)) {
      conflicts.push(field);
      continue;
    }
    if (incoming[field] !== undefined) {
      merged[field] = incoming[field];
    }
  }

  ["updatedAt", "lockVersion"].forEach((field) => {
    if (incoming[field] !== undefined) {
      merged[field] = incoming[field];
    }
  });

  return { merged, conflicts };
};

const initialCheckoutTask = [{}];

export const formatNotes = ({ notes = {}, finish = "" } = {}) => {
  // Clone the notes object to avoid mutating frozen Apollo Client cache objects
  const formattedNotes = { ...notes };

  // Add first initial description if array is empty
  if (!formattedNotes.descriptions?.length) {
    formattedNotes.descriptions = [initialDescription];
  }

  if (!formattedNotes.finishes?.length) {
    formattedNotes.finishes = [finish];
  }

  if (!formattedNotes.fabrics?.length) {
    formattedNotes.fabrics = [""];
  }

  if (!formattedNotes.laminates?.length) {
    formattedNotes.laminates = [""];
  }

  if (!formattedNotes.attic_stock) {
    formattedNotes.attic_stock = false;
  }

  if (!formattedNotes.physical_dimensions) {
    formattedNotes.physical_dimensions = [initialPhysicalDimensions];
  }

  if (!formattedNotes.checkout_task) {
    formattedNotes.checkout_task = initialCheckoutTask;
  } else {
    formattedNotes.checkout_task = formattedNotes.checkout_task.map((v) => ({
      ...v,
      checkin_date: v.checkin_date ? moment(v.checkin_date).format("yyyy-MM-DDThh:mm") : "",
      checkout_date: v.checkout_date ? moment(v.checkout_date).format("yyyy-MM-DDThh:mm") : "",
      requested_by_date: v.requested_by_date
        ? moment(v.requested_by_date).format("yyyy-MM-DDThh:mm")
        : "",
    }));
  }

  return formattedNotes;
};

export const getFields = (value) => {
  return [
    ...formatObject(value.include || {}),
    ...formatObject(value.descriptive_info || {}),
    ...formatObject(value.origin_info || {}),
    ...formatObject(value.destination_info || {}),
    ...formatObject(value.warehouse_info || {}),
    ...formatObject(value.electrical_info || {}),
    ...formatObject(value.plumbing_info || {}),
    ...formatObject(value.mechanical_info || {}),
    ...formatObject(value.other_info || {}),
    ...formatObject(value.configurable_custom_fields || {}),
    ...formatObject(value.inventory_items || {}),
    ...formatObject(value.orders || {}),
    ...formatObject(value.reservation || {}),
    ...formatObject(value.custom_fields || {}),
  ];
};

export const getDisplayFields = (value) =>
  getFields(value).filter((item) => !SKIP_FIELDS.includes(item));

export const formatCustomFields = (customFieldsValues, asset) => {
  if (!customFieldsValues || !asset?.id) return [];

  const customFieldValues = Object.entries(customFieldsValues).filter(
    ([key]) => key.startsWith("field_") && /field_\d+/.test(key)
  );

  return customFieldValues.map(([key, value]) => {
    const customFieldTemplateId = key.replace("field_", "");
    const existingField = asset.customFields?.find(
      (field) => String(field.customFieldTemplate?.id) === customFieldTemplateId
    );

    const fieldValue = value?.toString() ?? "";

    return existingField
      ? {
          id: existingField.id,
          value: fieldValue,
          order: existingField.order || 1,
        }
      : {
          custom_field_template_id: parseInt(customFieldTemplateId),
          value: fieldValue,
          order: 1,
        };
  });
};

export const prepareAssetInputWithCustomFields = (values, asset) => {
  const { customFields, ...formValues } = values;
  const customFieldsFormatted = formatCustomFields(customFields, asset);
  const input = { ...formValues };

  if (Object.prototype.hasOwnProperty.call(formValues, "lastIn")) {
    input.lastIn = formatDateForServer(formValues.lastIn);
  }

  if (customFieldsFormatted.length > 0) {
    input.customFields = customFieldsFormatted;
  }

  return input;
};

export const getCustomDescriptionValue = (asset) => {
  const customDescriptionField = (asset?.customFields || []).find((field) =>
    field?.label?.toLowerCase().includes("description")
  );

  return customDescriptionField?.value || "";
};

export const fetchAssetValues = (asset) => {
  if (!asset?.id) return {};

  const assetFields = Object.keys(displayFields).reduce(
    (result, item) =>
      displayFields[item] && item !== "photo"
        ? { ...result, [snakeCase(item)]: asset[snakeCase(item)] }
        : result,
    {}
  );

  const customFields = Object.fromEntries(
    (asset.customFields || []).map((field) => [
      `field_${field.customFieldTemplate.id}`,
      field.value,
    ])
  );

  const baseFields = {
    ...assetFields,
    active: asset.active,
    assetId: asset.assetId || "",
    barcode: asset.barcode || "",
    existingBuilding: asset.existingBuilding || "",
    newBuilding: asset.newBuilding || "",
    existingFloor: asset.existingFloor || "",
    newFloor: asset.newFloor || "",
    existingRoom: asset.existingRoom || "",
    newRoom: asset.newRoom || "",
    description: asset.description || "",
    descriptionComments: asset.descriptionComments || "",
    manufacturer: asset.manufacturer || "",
    condition: asset.condition || "",
    color: asset.color || "",
    employeeName: asset.employeeName || "",
    originComments: asset.originComments || "",
    destinationComments: asset.destinationComments || "",
    originEmployeeName: asset.originEmployeeName || "",
    destinationEmployeeName: asset.destinationEmployeeName || "",
    originRoomNumber: asset.originRoomNumber || "",
    destinationRoomNumber: asset.destinationRoomNumber || "",
    quantity: asset.quantity || 0,
    destinationQuantity: asset.destinationQuantity || 0,
    disposition: asset.disposition || "",
    originCubbyNumber: asset.originCubbyNumber || "",
    notes: formatNotes(asset),
    departmentName: asset.departmentName || "",
    serialNumber: asset.serialNumber || "",
    destinationNumber: asset.destinationNumber || "",
    installResponsibility: asset.installResponsibility || "",
    palletType: asset.palletType || "",
    unitType: asset.unitType || "",
    serialNumberUrl: asset.serialNumberUrl,
    quantity_ordered: asset.quantity_ordered,
    quantity_received: asset.quantity_received,
    quantityOrdered: asset.quantityOrdered,
    quantityReceived: asset.quantityReceived,
    itemCode: asset.itemCode,
    unitCost: asset.unitCost,
    poNumber: asset.poNumber,
    category: asset.category,
    subcategory: asset.subcategory,
    minimumQuantity: asset.minimumQuantity,
    weight: asset.weight,
    assetTotalWeight: asset.assetTotalWeight,
    osdNotes: asset.osdNotes,
    generalNotes: asset.generalNotes,
    inventoryNumber: asset.inventoryNumber,
    skuNumber: asset.skuNumber,
    batchNumber: asset.batchNumber,
    billingType: asset.billingType,
    manufacturePartNumber: asset.manufacturePartNumber,
    manufactureDate: asset.manufactureDate,
    carrierName: asset.carrierName || "",
    orderNumber: asset.orderNumber || "",
    bolNumber: asset.bolNumber || "",
    trackingNumber: asset.trackingNumber || "",
    truckNumber: asset.truckNumber || "",
    expirationDate: asset.expirationDate,
    expirationMonthDayYear: asset.expirationMonthDayYear,
    expirationMonth: asset.expirationMonth,
    expirationYear: asset.expirationYear,
    lastIn: formatMonthDayYear(asset.lastIn),
    unitOfMeasure: asset.unitOfMeasure,
    squareFootage: asset.squareFootage || "",
    squareFootageType: asset.squareFootageType || "",
    cubicFootage: asset.cubicFootage || "",
    predefinedCubicFeet: asset.predefinedCubicFeet || "",
    importedCustomAttributes: asset.importedCustomAttributes || "",
    rackLocations: asset.rackLocations || [],
    copyLocation: false,
    skipNew: false,
    customFields,

    // Administrative custom fields
  };

  if (asset.costCenter?.id) {
    baseFields.costCenterId = parseInt(asset.costCenter.id);
  }

  return baseFields;
};
